import { AdPreview } from '@/components/ui/ad-preview'
import {
    getAssetVariantForFormat,
    normalizeCreativeFormat,
    parseJsonList,
    processCreativeAssets,
} from '@/utils/creative-preview'

export interface PreviewCreative {
    id: string
    name?: string | null
    format?: string | null
    primary_texts?: unknown
    headlines?: unknown
    descriptions?: unknown
    call_to_action_type?: string | null
    display_link?: string | null
    destination_url?: string | null
    show_instant_form?: boolean | null
    adAssets?: Array<{ variations?: unknown[] | null; [key: string]: unknown }> | null
}

interface CreativeAdPreviewProps {
    creative: PreviewCreative
    pageTitle?: string
    pageIcon?: string
    autoPlay?: boolean
    playWithSound?: boolean
}

/**
 * The ad mockup of a creative (page, primary text, media, headline, description and call to action), built from the
 * creative as the My Creatives page loads it. Shared by the My Creatives cards and the Ads Manager hover preview.
 */
const CreativeAdPreview = ({
    creative,
    pageTitle,
    pageIcon,
    autoPlay = false,
    playWithSound = false,
}: CreativeAdPreviewProps) => {
    const assets = creative.adAssets || []
    const assetVariations = assets.flatMap((asset) => asset.variations || [])
    if (assetVariations.length === 0) return null

    const format = normalizeCreativeFormat(creative.format)
    const { assetData, carouselAssets } = processCreativeAssets(assets, format)

    return (
        <AdPreview
            primaryText={parseJsonList(creative.primary_texts)[0] || ''}
            headline={parseJsonList(creative.headlines)[0] || creative.name || 'Untitled Creative'}
            description={parseJsonList(creative.descriptions)[0] || ''}
            cta={creative.call_to_action_type || 'Learn More'}
            format={format}
            pageTitle={pageTitle}
            pageIcon={pageIcon}
            assetData={assetData}
            assetVariant={getAssetVariantForFormat(assetVariations, 'square')}
            carouselAssets={carouselAssets}
            showDefaultPrimaryText={false}
            displayLink={creative.display_link || ''}
            destinationUrl={creative.destination_url || ''}
            showInstantForm={creative.show_instant_form || false}
            assetVariations={assetVariations}
            adCreativeFormat={creative.format?.toLowerCase()}
            autoPlay={autoPlay}
            playWithSound={playWithSound}
        />
    )
}

interface CreativeHoverPreviewProps extends Pick<CreativeAdPreviewProps, 'creative' | 'pageTitle' | 'pageIcon'> {
    scale?: number
}

// Mockup shown while hovering a creative, enlarged by `scale`; videos play with sound
export const CreativeHoverPreview = ({ creative, pageTitle, pageIcon, scale = 1.4 }: CreativeHoverPreviewProps) => (
    <div className="tw:flex tw:justify-center">
        <div data-testid="creative-hover-preview" style={{ transform: `scale(${scale})`, transformOrigin: 'top' }}>
            <CreativeAdPreview creative={creative} pageTitle={pageTitle} pageIcon={pageIcon} playWithSound />
        </div>
    </div>
)

export default CreativeAdPreview
